import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "../lib/api";
import { setBackendSync, syncFromBackend, clearLocal } from "../lib/favourites";
import { setFlagSync, syncFlagsFromBackend, clearLocalFlags } from "../lib/flags";
import { BillingDialog } from "../components/BillingDialog";
import { PricingDialog } from "../components/PricingDialog";

const Ctx = createContext(null);
export const useAuth = () => useContext(Ctx);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingOpen, setBillingOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const u = await authApi.me();
      setUser(u);
      setBackendSync(true);
      setFlagSync(true);
      syncFromBackend();
      syncFlagsFromBackend();
      return u;
    } catch {
      setUser(null);
      setBackendSync(false);
      setFlagSync(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth, AuthCallback establishes the session first — skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = () => {
    const redirectUrl = window.location.origin + "/";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await authApi.logout().catch(() => {});
    setUser(null);
    setBackendSync(false);
    setFlagSync(false);
    clearLocal();
    clearLocalFlags();
    setBillingOpen(false);
    navigate("/signin");
  };

  const openBilling = () => setBillingOpen(true);
  const openPricing = () => setPricingOpen(true);

  return (
    <Ctx.Provider value={{ user, loading, login, logout, refresh, openBilling, openPricing }}>
      {children}
      <BillingDialog
        open={billingOpen}
        onOpenChange={setBillingOpen}
        user={user}
        refresh={refresh}
        onLogout={logout}
        onPricing={() => {
          setBillingOpen(false);
          setPricingOpen(true);
        }}
      />
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </Ctx.Provider>
  );
};
